import { getOrgAgents, getOrgPlaybooks, postEntitiesSearch } from '@onefootprint/fetch-dashboard';
import type { AgentResponse, Entity, Playbook } from '@onefootprint/request-types/dashboard';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { createSuggestionExtension, debouncePromise, setSuggestionLoading } from '@/components/mention-suggestion';
import MentionList from '../mention-list';
import MentionPill from './mention-pill';
import type { MentionAttrs, MentionItem, MentionKind } from './types';
import { getEntityHref, getEntityKindFromId, getEntityLabel } from './utils';
export { isSuggestionPopupOpen as isMentionPopupOpen } from '@/components/mention-suggestion';
export type { MentionAttrs, MentionItem, MentionKind } from './types';
export { getEntityHref, getEntityKindFromId, getIconForKind, mentionAttrsToResourceContext } from './utils';
const KIND_VALUES: MentionKind[] = ['person', 'business', 'playbook', 'investigation', 'agent_version'];
const isMentionKind = (value: unknown): value is MentionKind => typeof value === 'string' && (KIND_VALUES as string[]).includes(value);
export const Mention = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: el => (el as HTMLElement).getAttribute('data-id')
      },
      label: {
        default: null,
        parseHTML: el => (el as HTMLElement).textContent
      },
      kind: {
        default: 'person',
        parseHTML: el => {
          const attr = (el as HTMLElement).getAttribute('data-kind');
          if (isMentionKind(attr)) return attr;
          const id = (el as HTMLElement).getAttribute('data-id') ?? '';
          return getEntityKindFromId(id) ?? 'person';
        }
      }
    };
  },
  parseHTML() {
    return [{
      tag: 'a[data-mention]'
    }, {
      tag: 'span[data-mention]'
    }];
  },
  // Serialization fallback (clipboard copy, getHTML) — also rendered when no
  // NodeView is active. The rich pill UI lives in MentionPill (addNodeView).
  renderHTML({
    node,
    HTMLAttributes
  }) {
    const id: string = node.attrs.id ?? '';
    const kind: MentionKind = isMentionKind(node.attrs.kind) ? node.attrs.kind : getEntityKindFromId(id) ?? 'person';
    return ['a', mergeAttributes({
      'data-mention': 'true',
      'data-id': id,
      'data-kind': kind,
      href: getEntityHref(id, kind) ?? undefined,
      target: '_blank',
      rel: 'noreferrer'
    }, HTMLAttributes), id];
  },
  renderText({
    node
  }) {
    return node.attrs.id;
  },
  addNodeView() {
    return ReactNodeViewRenderer(MentionPill);
  }
});
const SEARCH_DEBOUNCE_MS = 250;
const AGENT_PAGE_SIZE = 5;
const ENTITY_PAGE_SIZE = 5;
const PLAYBOOK_PAGE_SIZE = 5;

// Scope entity searches to the env selected in the chat input (per-request
// `x-is-live` header); undefined → the client's global header. Agents are
// tenant-level and not env-scoped, so they never carry this header.
const envHeaders = (isLive: boolean | undefined) => isLive === undefined ? undefined : {
  'x-is-live': String(isLive)
};

// Backend entity `search` is kind-scoped, so we don't pass it an `fp_…` id —
// the caller routes id-prefix queries to `searchEntitiesById` instead.
const searchEntitiesByName = async (trimmed: string, isLive: boolean | undefined): Promise<Entity[]> => {
  const response = await postEntitiesSearch({
    body: {
      search: trimmed,
      pagination: {
        pageSize: ENTITY_PAGE_SIZE
      }
    },
    headers: envHeaders(isLive),
    throwOnError: true
  });
  return response.data?.data ?? [];
};

// Backend has no id-substring search, so fetch a page and filter client-side.
const searchEntitiesById = async (trimmed: string, isLive: boolean | undefined): Promise<Entity[]> => {
  const response = await postEntitiesSearch({
    body: {
      pagination: {
        pageSize: 50
      }
    },
    headers: envHeaders(isLive),
    throwOnError: true
  });
  const lower = trimmed.toLowerCase();
  return (response.data?.data ?? []).filter(e => e.id.toLowerCase().includes(lower)).slice(0, 8);
};

// Name-only search; `ag_…` id-prefix queries are intentionally not supported yet.
const searchAgents = async (trimmed: string): Promise<AgentResponse[]> => {
  const response = await getOrgAgents({
    query: {
      search: trimmed,
      pageSize: AGENT_PAGE_SIZE
    },
    throwOnError: true
  });
  return response.data?.data ?? [];
};

// Playbooks are env-scoped (live/sandbox) like entities, so the search carries the chat input's
// env header. Name-only; `pb_…` id-prefix queries aren't supported yet.
const searchPlaybooks = async (trimmed: string, isLive: boolean | undefined): Promise<Playbook[]> => {
  const response = await getOrgPlaybooks({
    query: {
      search: trimmed,
      pageSize: PLAYBOOK_PAGE_SIZE
    },
    headers: envHeaders(isLive),
    throwOnError: true
  });
  return response.data?.data ?? [];
};
const runMentionSearch = debouncePromise(async ({
  trimmed,
  isLive
}: {
  trimmed: string;
  isLive: boolean | undefined;
}): Promise<MentionItem[]> => {
  try {
    // Two paths:
    //   `fp_…` query → entity-id lookup (agents skipped — names can't match an fp_id).
    //   anything else → entity name search + agent name search in parallel.
    if (trimmed.toLowerCase().startsWith('fp_')) {
      const entities = await searchEntitiesById(trimmed, isLive);
      return entities.map(entity => ({
        type: 'entity',
        entity
      }));
    }
    const [entities, agents, playbooks] = await Promise.all([searchEntitiesByName(trimmed, isLive), searchAgents(trimmed), searchPlaybooks(trimmed, isLive)]);
    return [...agents.map(agent => ({
      type: 'agent',
      agent
    }) as const), ...playbooks.map(playbook => ({
      type: 'playbook',
      playbook
    }) as const), ...entities.map(entity => ({
      type: 'entity',
      entity
    }) as const)];
  } catch (err) {
    console.error('mention search failed', err);
    return [];
  }
}, SEARCH_DEBOUNCE_MS);
const toMentionAttrs = (item: MentionItem): MentionAttrs => {
  switch (item.type) {
    case 'agent':
      return {
        id: item.agent.id,
        label: item.agent.name,
        kind: 'agent'
      };
    case 'playbook':
      return {
        id: item.playbook.id,
        label: item.playbook.publishedVersion.name,
        kind: 'playbook'
      };
    default:
      return {
        id: item.entity.id,
        label: getEntityLabel(item.entity),
        kind: item.entity.kind === 'business' ? 'business' : 'person'
      };
  }
};
export const MentionSuggestion = createSuggestionExtension<MentionItem>({
  name: 'mentionSuggestion',
  char: '@',
  // Multi-word names (agents like "KYC review", people like "James Chen") need
  // spaces inside the query. Exit by selecting a result, pressing Escape, or
  // backspacing through the `@`.
  allowSpaces: true,
  items: async ({
    query,
    editor,
    isLive
  }) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestionLoading(editor, false);
      return [];
    }
    setSuggestionLoading(editor, true);
    // Loading is cleared automatically when MentionList receives the
    // fresh `items` array via TipTap's onUpdate (see useEffect in mention-list).
    return runMentionSearch({
      trimmed,
      isLive
    });
  },
  command: ({
    editor,
    range,
    item
  }) => {
    const attrs = toMentionAttrs(item);
    editor.chain().focus().insertContentAt(range, [{
      type: 'mention',
      attrs
    }, {
      type: 'text',
      text: ' '
    }]).run();
  },
  inlineWhenEmpty: true,
  renderList: ({
    items,
    query,
    onSelect,
    listRef
  }) => <MentionList ref={listRef} items={items} query={query} onSelect={onSelect} />
});